/**
 * Web loading state — the twin of `loading-state.tsx`.
 *
 * Nova, a shimmering status label and a live elapsed timer in mono tabular
 * figures. This is what the user sees between hitting send and the first
 * thing arriving: the face names the wait (thinking, writing, rewriting) and
 * the ticking number is the honest signal that the connection is alive.
 *
 * The timer is hidden from screen readers and kept out of the polite live
 * region so nothing is announced ten times a second.
 */
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useElapsedLabel } from './agent-run-timing';
import { NovaIcon } from './nova-loader.web';
import type { NovaStatus } from './nova-loader-motion';
import { ShimmerText } from './shimmer-text.web';

export const LoadingState: FC<{
  label: string;
  /** Epoch ms the wait started; `null` hides the timer. */
  startedAt: number | null;
  status?: NovaStatus;
  className?: string;
}> = ({ label, startedAt, status = 'thinking', className }) => {
  const { t } = useTranslation('common');
  const elapsed = useElapsedLabel(startedAt, true, t);

  return (
    <div
      className={['tabbio-loading-state nova-loader', className].filter(Boolean).join(' ')}
      data-status={status}
    >
      <NovaIcon status={status} size={20} />
      <ShimmerText className="tabbio-loading-label" role="status" aria-live="polite" aria-atomic="true">
        {label}
      </ShimmerText>
      {elapsed ? (
        <span className="tabbio-elapsed" dir="ltr" aria-hidden="true">
          {elapsed}
        </span>
      ) : null}
    </div>
  );
};

export default LoadingState;
